import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLogin from '../components/admin/AdminLogin';
import AdminLeadsViewer from '../components/admin/AdminLeadsViewer';
import AdminNav from '../components/admin/AdminNav';
import { getSesion } from '../utils/sesionAdmin';
import { llevarSetterASetting, setterAlEntrar } from '../utils/setterAdmin';
import '../styles/admin.css';
import { ADMIN_API } from '../utils/adminApi';


const AdminLeadsPage = () => {
  const [stage, setStage] = useState('checking'); // 'checking' | 'login' | 'viewer' | 'error'
  const [data, setData] = useState(null);
  const [incompletos, setIncompletos] = useState([]);
  const [reloading, setReloading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [sesion, setSesion] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      // En paralelo: la sesión no le suma latencia a la carga de leads.
      const [res, ses] = await Promise.all([
        axios.get(`${ADMIN_API}/api/admin/leads`, { withCredentials: true }),
        getSesion(),
      ]);
      setData(res.data);
      setSesion(ses);

      // Solo admin. Va aparte del Promise.all porque depende del rol, que sale
      // de la sesión, y porque un 503 de esta hoja no puede dejar a Agustín
      // sin panel: se traga el error y la lista queda vacía.
      if (ses?.rol === 'admin') {
        try {
          const inc = await axios.get(
            `${ADMIN_API}/api/admin/leads/incompletos`,
            { withCredentials: true },
          );
          setIncompletos(inc.data.incompletos || []);
        } catch (e) {
          console.error('No se pudieron leer los formularios incompletos:', e?.message);
          setIncompletos([]);
        }
      } else {
        // Si el rol no es admin (o cambió desde la última carga, ej. un
        // logout/login con otra cuenta sin recargar la página), no queda
        // ningún dato viejo de incompletos dando vueltas en memoria: el gate
        // de la vista es una segunda barrera, no la única.
        setIncompletos([]);
      }

      setStage('viewer');
    } catch (err) {
      if (err?.response?.status === 401) {
        setStage('login');
      } else if (err?.response?.status === 403 && await llevarSetterASetting()) {
        // Un setter no tiene nada acá: se fue a /admin/setting.
      } else {
        setErrMsg(err?.response?.data?.error || 'No se pudo conectar con el servidor.');
        setStage('error');
      }
    }
  }, []);

  const reload = async () => {
    setReloading(true);
    await fetchLeads();
    setReloading(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${ADMIN_API}/api/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Ignorar error de logout — igual mostramos login
    }
    setData(null);
    setStage('login');
  };

  const toggleContactado = async (lead, nextContactado) => {
    const targetTimestamp = nextContactado
      ? new Date().toLocaleString('es-CL', { timeZone: 'America/Santiago' })
      : '';

    const matches = (l) => l.email === lead.email && l.fecha === lead.fecha;
    const apply = (timestamp) => (l) => (matches(l) ? { ...l, contactado: timestamp } : l);

    const previousTimestamp = lead.contactado || '';

    // 1. Optimistic update
    setData((prev) => ({
      ...prev,
      calificados: (prev.calificados || []).map(apply(targetTimestamp)),
      noCalificados: (prev.noCalificados || []).map(apply(targetTimestamp)),
    }));

    // 2. POST al backend
    try {
      await axios.post(
        `${ADMIN_API}/api/admin/leads/contactado`,
        { email: lead.email, fecha: lead.fecha, contactado: nextContactado },
        { withCredentials: true },
      );
    } catch (err) {
      // 3. Revertir
      setData((prev) => ({
        ...prev,
        calificados: (prev.calificados || []).map(apply(previousTimestamp)),
        noCalificados: (prev.noCalificados || []).map(apply(previousTimestamp)),
      }));
      // eslint-disable-next-line no-alert
      alert('No se pudo guardar el cambio. Intenta de nuevo.');
      throw err;
    }
  };

  /**
   * Guarda el resultado de la llamada. Mismo patrón optimista que
   * toggleContactado: se pinta al toque y se revierte si el POST falla.
   *
   * El match es SOLO por email, no por email + fecha como en contactado: el
   * resultado vive en "Agenda Llamadas", que tiene una fila por persona, y un
   * mismo correo puede tener varias filas en Hoja 1 (el lead reenvió el
   * formulario). Todas tienen que mostrar lo mismo.
   */
  const setResultado = async (lead, resultado, nota, contactarEl, cerroCon) => {
    const matches = (l) => l.email === lead.email;
    const apply = (bot) => (l) => (matches(l) && l.bot ? { ...l, bot: { ...l.bot, ...bot } } : l);

    const previo = {
      resultado: lead.bot?.resultado || '',
      nota: lead.bot?.nota || '',
      contactarEl: lead.bot?.contactarEl || '',
      cerroCon: lead.bot?.cerroCon || '',
    };
    const siguiente = {
      resultado,
      ...(nota !== undefined ? { nota } : {}),
      ...(contactarEl !== undefined ? { contactarEl } : {}),
      // Solo un contrato conserva el coach de cierre: el backend lo borra al
      // pasar a cualquier otro resultado, y el optimismo tiene que pintar lo
      // mismo que va a quedar guardado.
      ...(cerroCon !== undefined ? { cerroCon } : {}),
      ...(resultado !== 'contrato' ? { cerroCon: '' } : {}),
    };

    setData((prev) => ({
      ...prev,
      calificados: (prev.calificados || []).map(apply(siguiente)),
      noCalificados: (prev.noCalificados || []).map(apply(siguiente)),
    }));

    try {
      await axios.post(
        `${ADMIN_API}/api/admin/leads/resultado`,
        {
          email: lead.email,
          resultado,
          ...(nota !== undefined ? { nota } : {}),
          ...(contactarEl !== undefined ? { contactarEl } : {}),
          ...(cerroCon !== undefined ? { cerroCon } : {}),
        },
        { withCredentials: true },
      );
    } catch (err) {
      setData((prev) => ({
        ...prev,
        calificados: (prev.calificados || []).map(apply(previo)),
        noCalificados: (prev.noCalificados || []).map(apply(previo)),
      }));
      // eslint-disable-next-line no-alert
      alert('No se pudo guardar el resultado. Intenta de nuevo.');
      throw err;
    }
  };

  /**
   * Marca contactado a alguien que quedó a medias en el formulario. Mismo
   * patrón optimista que toggleContactado y setResultado: se pinta al toque
   * y se revierte si el POST falla.
   *
   * El backend distingue 404 de 503 A PROPÓSITO (ver markContactadoIncompleto
   * en adminLeadsController.js): 404 es que esa persona no está en la hoja
   * (dato raro, el admin debe saberlo tal cual) y 503 es que no se pudo
   * escribir (hay que reintentar en un rato). Colapsar los dos en un mensaje
   * genérico acá arriba borraría esa distinción justo en la capa donde el
   * humano la lee.
   */
  const contactarIncompleto = async (email) => {
    const timestamp = new Date().toLocaleString('es-CL', { timeZone: 'America/Santiago' });
    const quien = sesion?.nombre || 'admin';
    const apply = (valor) => (i) => (i.email === email ? { ...i, ...valor } : i);

    const previo = incompletos.find((i) => i.email === email) || {};
    const previoValores = {
      contactadoPor: previo.contactadoPor || '',
      contactadoEl: previo.contactadoEl || '',
    };

    setIncompletos((prev) => prev.map(apply({ contactadoPor: quien, contactadoEl: timestamp })));

    try {
      await axios.post(
        `${ADMIN_API}/api/admin/leads/incompletos/contactado`,
        { email },
        { withCredentials: true },
      );
    } catch (err) {
      setIncompletos((prev) => prev.map(apply(previoValores)));
      const status = err?.response?.status;
      let mensaje = 'No se pudo marcar como contactado. Intenta de nuevo.';
      if (status === 404) {
        mensaje = 'Esa persona ya no está en la hoja de formularios incompletos.';
      } else if (status === 503) {
        mensaje = 'No se pudo escribir en la hoja de formularios incompletos. Intenta de nuevo en un momento.';
      }
      // eslint-disable-next-line no-alert
      alert(mensaje);
      throw err;
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  if (stage === 'checking') {
    return (
      <div className="au-admin au-admin-shell">
        <div className="au-week-empty">&gt;&gt; Conectando…</div>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className="au-admin au-admin-shell">
        <div className="au-week-empty">
          &gt;&gt; {errMsg}{' '}
          <button
            onClick={() => {
              setStage('checking');
              fetchLeads();
            }}
            style={{
              marginLeft: 12,
              background: 'transparent',
              border: '1px solid #1F2533',
              color: '#F8FAFC',
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: 12,
              padding: '6px 12px',
              cursor: 'pointer',
            }}
          >
            REINTENTAR
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'login') {
    return (
      <AdminLogin onSuccess={(datos) => { if (setterAlEntrar(datos)) return; setStage('checking'); fetchLeads(); }} />
    );
  }

  return (
    <>
      <div className="au-admin au-admin-nav-wrap">
        <AdminNav actual="leads" rol={sesion?.rol} />
      </div>
      <AdminLeadsViewer
        data={data}
        rol={sesion?.rol}
        incompletos={incompletos}
        onReload={reload}
        onLogout={logout}
        reloading={reloading}
        onToggleContactado={toggleContactado}
        onSetResultado={setResultado}
        onContactarIncompleto={contactarIncompleto}
      />
    </>
  );
};

export default AdminLeadsPage;
